'use client';
import React, { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import {
  ArrowLeft,
  Save,
  Loader2,
  Package,
} from 'lucide-react';
import Link from 'next/link';
import { toast } from 'sonner';
import {
  type PriceResearch,
} from '@/lib/claimStore';
import { fetchRooms, fetchFamilyMembers, insertClaimItem, fetchClaimCategories, insertClaimCategory, type ClaimCategory } from '@/lib/claimService';
import { useChat } from '../../../lib/hooks/useChat';
import VoiceInputButton from './VoiceInputButton';
import PriceResearchPanel from './PriceResearchPanel';
import PasteDetector from './PasteDetector';
import ImportItemsModal from './ImportItemsModal';
import { useClaimContext } from '@/contexts/ClaimContext';

interface ItemFormValues {
  name: string;
  description: string;
  quantity: number;
  location: string;
  addedBy: string;
  condition: string;
  estimatedAge: string;
  notes: string;
  unitPrice: string;
  category: string;
}

const CONDITIONS = ['Destroyed', 'Severely Damaged', 'Moderately Damaged', 'Minor Damage', 'Unknown'];

export default function ItemEntryContent() {
  const router = useRouter();
  const { activeClaim } = useClaimContext();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [priceResearch, setPriceResearch] = useState<PriceResearch | null>(null);
  const [isResearching, setIsResearching] = useState(false);
  const [rooms, setRooms] = useState<{ id: string; name: string }[]>([]);
  const [members, setMembers] = useState<{ id: string; name: string; color: string }[]>([]);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [showImportModal, setShowImportModal] = useState(false);
  const researchPendingRef = useRef(false);
  const researchedItemNameRef = useRef<string>('');

  // Categories
  const [categories, setCategories] = useState<ClaimCategory[]>([]);
  const [newCategoryInput, setNewCategoryInput] = useState('');
  const [addingCategory, setAddingCategory] = useState(false);
  const [savingCategory, setSavingCategory] = useState(false);
  const categoryInputRef = useRef<HTMLInputElement>(null);

  const { response, fullResponse, isLoading: isChatLoading, error: chatError, sendMessage } = useChat(
    'PERPLEXITY',
    'perplexity/sonar-pro',
    false
  );

  useEffect(() => {
    if (chatError) {
      toast.error(chatError.message ?? 'Price research failed. Please try again.');
      setIsResearching(false);
      researchPendingRef.current = false;
    }
  }, [chatError]);

  // Parse Perplexity response once it arrives
  useEffect(() => {
    if (!researchPendingRef.current || isChatLoading || !response) return;
    researchPendingRef.current = false;

    // Only apply results if the item name hasn't changed since research started
    const currentName = watch('name');
    if (currentName !== researchedItemNameRef.current) {
      setIsResearching(false);
      return;
    }

    try {
      // Extract price — look for dollar amounts like $1,299.99 or 1299.99
      const priceMatch = response.match(/\$[\d,]+(?:\.\d{1,2})?/);
      const rawPrice = priceMatch ? parseFloat(priceMatch[0].replace(/[$,]/g, '')) : 0;

      // Extract source citations from fullResponse
      const searchResults = (fullResponse as any)?.search_results ?? [];
      const citations = (fullResponse as any)?.citations ?? [];
      const annotations = (fullResponse as any)?.choices?.[0]?.message?.annotations ?? [];

      // Pick the best source URL and name
      // Priority: annotations (specific page URLs) > citations > search_results (often base URLs)
      let sourceName = 'Web Research';
      let sourceUrl = '';

      if (annotations.length > 0) {
        const firstAnnotation = annotations[0]?.url_citation;
        sourceUrl = firstAnnotation?.url ?? '';
        sourceName = firstAnnotation?.title ?? (sourceUrl ? (() => { try { return new URL(sourceUrl).hostname; } catch { return 'Web Research'; } })() : 'Web Research');
      } else if (citations.length > 0) {
        sourceUrl = citations[0];
        try { sourceName = new URL(citations[0]).hostname; } catch { /* keep default */ }
      } else if (searchResults.length > 0) {
        sourceUrl = searchResults[0].url ?? '';
        sourceName = searchResults[0].title ?? (() => { try { return new URL(searchResults[0].url).hostname; } catch { return 'Web Research'; } })();
      }

      // Build additional sources list for notes
      // Priority: annotations > citations > search_results
      const allSources: { name: string; url: string }[] = [];
      if (annotations.length > 0) {
        annotations.slice(0, 4).forEach((ann: any) => {
          const urlCitation = ann?.url_citation;
          if (urlCitation?.url) {
            allSources.push({ name: urlCitation.title ?? urlCitation.url, url: urlCitation.url });
          }
        });
      } else if (citations.length > 0) {
        citations.slice(0, 4).forEach((url: string) => {
          try { allSources.push({ name: new URL(url).hostname, url }); } catch { /* skip */ }
        });
      } else if (searchResults.length > 0) {
        searchResults.slice(0, 4).forEach((sr: any) => {
          if (sr.url) {
            allSources.push({ name: sr.title ?? sr.url, url: sr.url });
          }
        });
      }

      // Determine confidence based on how many sources were found
      const confidence: 'high' | 'medium' | 'low' =
        allSources.length >= 3 ? 'high' : allSources.length >= 1 ? 'medium' : 'low';

      const result: PriceResearch = {
        price: rawPrice,
        sourceName,
        sourceUrl,
        researchDate: new Date().toISOString().split('T')[0],
        confidence,
        notes: response.slice(0, 300).replace(/\n+/g, ' ').trim(),
        sources: allSources,
      };

      setPriceResearch(result);
      if (rawPrice > 0) {
        setValue('unitPrice', rawPrice.toString(), { shouldDirty: true });
        toast.success('Replacement price found');
      } else {
        toast.success('Research complete — please review and enter price manually');
      }
    } catch {
      toast.error('Could not parse price from research. Please enter manually.');
    } finally {
      setIsResearching(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [response, isChatLoading, fullResponse]);

  useEffect(() => {
    async function loadConfig() {
      const [roomData, memberData] = await Promise.all([fetchRooms(), fetchFamilyMembers()]);
      setRooms(roomData);
      setMembers(memberData);

      // Load categories for active claim
      if (activeClaim?.id) {
        const cats = await fetchClaimCategories(activeClaim.id);
        setCategories(cats);
      }

      setLoadingConfig(false);
    }
    loadConfig();
  }, [activeClaim?.id]);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<ItemFormValues>({
    defaultValues: {
      name: '',
      description: '',
      quantity: 1,
      location: '',
      addedBy: '',
      condition: 'Destroyed',
      estimatedAge: '',
      notes: '',
      unitPrice: '',
      category: '',
    },
  });

  // Set defaults once rooms/members load
  useEffect(() => {
    if (rooms.length > 0) setValue('location', rooms[0].name);
    if (members.length > 0) setValue('addedBy', members[0].name);
  }, [rooms, members, setValue]);

  const watchedName = watch('name');

  const handleVoiceTranscript = (transcript: string) => {
    const words = transcript.trim();
    if (!watch('name')) {
      setValue('name', words, { shouldDirty: true });
    } else {
      setValue('description', words, { shouldDirty: true });
    }
    toast.success('Voice input captured');
  };

  const handleResearchPrice = async () => {
    const name = watch('name');
    const description = watch('description');
    const category = watch('category');
    if (!name) {
      toast.error('Enter an item name before researching price');
      return;
    }

    setIsResearching(true);
    setPriceResearch(null);
    researchPendingRef.current = true;
    researchedItemNameRef.current = name;

    const parts: string[] = [];
    if (category) parts.push(category);
    parts.push(name);
    if (description) parts.push(description);
    const query = parts.join(' — ');

    sendMessage(
      [
        {
          role: 'system',
          content:
            'You are a retail price research assistant. When given an item name, search the web for its current MSRP (Manufacturer\'s Suggested Retail Price) and one retail price from a reputable new-goods retailer. Only use sources that sell brand-new products — do NOT use eBay, Amazon, Craigslist, Facebook Marketplace, OfferUp, or any marketplace that sells used, refurbished, or reconditioned items. Respond with: 1) The MSRP in USD (e.g. "MSRP: $299.99"), 2) One retail price from a reputable new-goods retailer in USD (e.g. "Retail: $279.99 at [Store Name]"), 3) The store name and URL for the retail price, 4) A brief one-sentence note about the pricing. Always include both dollar amounts.',
        },
        {
          role: 'user',
          content: `What is the MSRP and current retail price (new) for: ${query}? Provide both the MSRP and one retail price from a reputable retailer that sells only new products (not eBay, Amazon, or any used/reconditioned marketplace). Include the store name and URL.`,
        },
      ],
      {
        temperature: 0.2,
        max_tokens: 512,
        web_search_options: { search_context_size: 'medium' },
      }
    );
  };

  const handlePastedItems = (itemNames: string[]) => {
    if (itemNames.length > 0) {
      setValue('name', itemNames[0], { shouldDirty: true });
      if (itemNames.length > 1) {
        toast.info(`${itemNames.length} items detected. Fill in details and save each one.`);
      }
    }
  };

  const handleAddCategory = async () => {
    const trimmed = newCategoryInput.trim();
    if (!trimmed || !activeClaim?.id) return;
    setSavingCategory(true);
    const created = await insertClaimCategory(activeClaim.id, trimmed);
    if (created) {
      setCategories((prev) => [...prev, created]);
      setValue('category', created.name, { shouldDirty: true });
      setNewCategoryInput('');
      setAddingCategory(false);
    } else {
      // Category may already exist — just select it
      const existing = categories.find((c) => c.name.toLowerCase() === trimmed.toLowerCase());
      if (existing) setValue('category', existing.name, { shouldDirty: true });
      setNewCategoryInput('');
      setAddingCategory(false);
    }
    setSavingCategory(false);
  };

  const onSubmit = async (data: ItemFormValues) => {
    setIsSubmitting(true);
    const saved = await insertClaimItem({
      name: data.name,
      description: data.description,
      quantity: data.quantity,
      location: data.location,
      addedBy: data.addedBy,
      condition: data.condition,
      estimatedAge: data.estimatedAge,
      notes: data.notes,
      priceStatus: priceResearch ? 'priced' : 'unpriced',
      unitPrice: data.unitPrice ? parseFloat(data.unitPrice) : 0,
      priceResearch: priceResearch,
      dateAdded: new Date().toISOString().split('T')[0],
      inputMethod: 'typed',
      category: data.category,
    }, activeClaim?.id);

    if (saved) {
      toast.success(`"${data.name}" added to inventory`);
      reset({ name: '', description: '', quantity: 1, location: rooms[0]?.name ?? '', addedBy: members[0]?.name ?? '', condition: 'Destroyed', estimatedAge: '', notes: '', unitPrice: '', category: data.category });
      setPriceResearch(null);
      router.push('/');
    } else {
      toast.error('Failed to save item. Please try again.');
    }
    setIsSubmitting(false);
  };

  const handleSaveAndAddAnother = handleSubmit(async (data) => {
    setIsSubmitting(true);
    const saved = await insertClaimItem({
      name: data.name,
      description: data.description,
      quantity: data.quantity,
      location: data.location,
      addedBy: data.addedBy,
      condition: data.condition,
      estimatedAge: data.estimatedAge,
      notes: data.notes,
      priceStatus: priceResearch ? 'priced' : 'unpriced',
      unitPrice: data.unitPrice ? parseFloat(data.unitPrice) : 0,
      priceResearch: priceResearch,
      dateAdded: new Date().toISOString().split('T')[0],
      inputMethod: 'typed',
      category: data.category,
    }, activeClaim?.id);
    if (saved) {
      toast.success(`"${data.name}" added — ready for next item`);
      reset({ name: '', description: '', quantity: 1, location: data.location, addedBy: data.addedBy, condition: data.condition, estimatedAge: '', notes: '', unitPrice: '', category: data.category });
      setPriceResearch(null);
    } else {
      toast.error('Failed to save item. Please try again.');
    }
    setIsSubmitting(false);
  });

  const handleImportComplete = (count: number) => {
    setShowImportModal(false);
    router.push('/');
  };

  return (
    <div className="space-y-6 fade-in">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link
          href="/"
          className="p-2 rounded-lg hover:bg-secondary transition-colors text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft size={18} />
        </Link>
        <div className="flex-1">
          <h1 className="text-2xl font-semibold text-foreground">Add Inventory Item</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Document a destroyed or damaged item for your insurance claim
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowImportModal(true)}
          className="flex items-center gap-2 px-4 py-2 border border-border rounded-lg text-sm font-medium text-foreground hover:bg-secondary transition-colors"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
          Import CSV / Excel
        </button>
      </div>

      {/* Input method bar */}
      <div className="bg-card border border-border rounded-xl p-4">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Quick Entry Methods</p>
        <div className="flex flex-wrap gap-3 items-start">
          <VoiceInputButton onTranscript={handleVoiceTranscript} />
          <div className="flex-1 min-w-[280px]">
            <PasteDetector onParsedItems={handlePastedItems} />
          </div>
        </div>
      </div>

      {/* Main form + research panel */}
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="grid grid-cols-1 xl:grid-cols-3 2xl:grid-cols-3 gap-6">
          {/* Left: Form fields — spans 2 cols */}
          <div className="xl:col-span-2 space-y-5">
            {/* Item details card */}
            <div className="bg-card border border-border rounded-xl p-5 space-y-5">
              <div className="flex items-center gap-2 pb-2 border-b border-border">
                <Package size={16} className="text-primary" />
                <h2 className="text-sm font-semibold text-foreground">Item Details</h2>
              </div>

              {/* Item name */}
              <div className="space-y-1">
                <label htmlFor="name" className="block text-sm font-medium text-foreground">
                  Item Name <span className="text-negative">*</span>
                </label>
                <p className="text-xs text-muted-foreground">Be specific — include brand, model, and size if known</p>
                <input
                  id="name"
                  type="text"
                  placeholder="e.g. Samsung 65-inch QLED 4K TV, Model QN65Q80C"
                  className={`w-full px-3 py-2.5 text-sm border rounded-lg bg-input focus:outline-none focus:ring-2 focus:ring-ring placeholder:text-muted-foreground ${errors.name ? 'border-negative focus:ring-negative/30' : 'border-border'}`}
                  {...register('name', { required: 'Item name is required' })}
                />
                {errors.name && (
                  <p className="text-xs text-negative">{errors.name.message}</p>
                )}
              </div>

              {/* Description */}
              <div className="space-y-1">
                <label htmlFor="description" className="block text-sm font-medium text-foreground">
                  Description
                </label>
                <p className="text-xs text-muted-foreground">Additional details, color, finish, accessories included</p>
                <textarea
                  id="description"
                  rows={3}
                  placeholder="e.g. Wall-mounted, black finish, includes remote and original box accessories"
                  className="w-full px-3 py-2.5 text-sm border border-border rounded-lg bg-input resize-none focus:outline-none focus:ring-2 focus:ring-ring placeholder:text-muted-foreground"
                  {...register('description')}
                />
              </div>

              {/* Quantity + Condition row */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label htmlFor="quantity" className="block text-sm font-medium text-foreground">
                    Quantity
                  </label>
                  <input
                    id="quantity"
                    type="number"
                    min={1}
                    className={`w-full px-3 py-2.5 text-sm border rounded-lg bg-input focus:outline-none focus:ring-2 focus:ring-ring font-mono ${errors.quantity ? 'border-negative' : 'border-border'}`}
                    {...register('quantity', {
                      min: { value: 1, message: 'Minimum 1' },
                      valueAsNumber: true,
                    })}
                  />
                  {errors.quantity && (
                    <p className="text-xs text-negative">{errors.quantity.message}</p>
                  )}
                </div>
                <div className="space-y-1">
                  <label htmlFor="condition" className="block text-sm font-medium text-foreground">
                    Condition
                  </label>
                  <select
                    id="condition"
                    className="w-full px-3 py-2.5 text-sm border border-border rounded-lg bg-input focus:outline-none focus:ring-2 focus:ring-ring"
                    {...register('condition')}
                  >
                    {CONDITIONS.map((c) => (
                      <option key={`cond-${c}`} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Category */}
              <div className="space-y-1">
                <label htmlFor="category" className="block text-sm font-medium text-foreground">
                  Category
                </label>
                {loadingConfig ? (
                  <div className="w-full px-3 py-2.5 text-sm border border-border rounded-lg bg-input text-muted-foreground">Loading…</div>
                ) : addingCategory ? (
                  <div className="flex gap-2">
                    <input
                      ref={categoryInputRef}
                      type="text"
                      value={newCategoryInput}
                      onChange={(e) => setNewCategoryInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') { e.preventDefault(); handleAddCategory(); }
                        if (e.key === 'Escape') { setAddingCategory(false); setNewCategoryInput(''); }
                      }}
                      placeholder="New category name…"
                      className="flex-1 px-3 py-2.5 text-sm border border-border rounded-lg bg-input focus:outline-none focus:ring-2 focus:ring-ring placeholder:text-muted-foreground"
                      autoFocus
                    />
                    <button
                      type="button"
                      onClick={handleAddCategory}
                      disabled={savingCategory || !newCategoryInput.trim()}
                      className="px-3 py-2 text-sm bg-primary text-primary-foreground rounded-lg hover:bg-amber-700 disabled:opacity-50 transition-colors"
                    >
                      {savingCategory ? <Loader2 size={14} className="animate-spin" /> : 'Add'}
                    </button>
                    <button
                      type="button"
                      onClick={() => { setAddingCategory(false); setNewCategoryInput(''); }}
                      className="px-3 py-2 text-sm border border-border rounded-lg text-muted-foreground hover:bg-secondary transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <select
                      id="category"
                      className="flex-1 px-3 py-2.5 text-sm border border-border rounded-lg bg-input focus:outline-none focus:ring-2 focus:ring-ring"
                      {...register('category')}
                    >
                      <option value="">— Select category —</option>
                      {categories.map((cat) => (
                        <option key={cat.id} value={cat.name}>{cat.name}</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => setAddingCategory(true)}
                      className="flex items-center gap-1 px-3 py-2 text-sm border border-border rounded-lg text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors whitespace-nowrap"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                      New
                    </button>
                  </div>
                )}
              </div>

              {/* Estimated age */}
              <div className="space-y-1">
                <label htmlFor="estimatedAge" className="block text-sm font-medium text-foreground">
                  Estimated Age
                </label>
                <input
                  id="estimatedAge"
                  type="text"
                  placeholder="e.g. 2 years, 6 months"
                  className="w-full px-3 py-2.5 text-sm border border-border rounded-lg bg-input focus:outline-none focus:ring-2 focus:ring-ring placeholder:text-muted-foreground"
                  {...register('estimatedAge')}
                />
              </div>

              {/* Notes */}
              <div className="space-y-1">
                <label htmlFor="notes" className="block text-sm font-medium text-foreground">
                  Notes
                </label>
                <textarea
                  id="notes"
                  rows={2}
                  placeholder="Model number, serial number, purchase location, any other relevant details"
                  className="w-full px-3 py-2.5 text-sm border border-border rounded-lg bg-input resize-none focus:outline-none focus:ring-2 focus:ring-ring placeholder:text-muted-foreground"
                  {...register('notes')}
                />
              </div>
            </div>

            {/* Location + Member card */}
            <div className="bg-card border border-border rounded-xl p-5 space-y-4">
              <h2 className="text-sm font-semibold text-foreground pb-2 border-b border-border">Location &amp; Attribution</h2>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label htmlFor="location" className="block text-sm font-medium text-foreground">
                    Room / Location
                  </label>
                  {loadingConfig ? (
                    <div className="w-full px-3 py-2.5 text-sm border border-border rounded-lg bg-input text-muted-foreground">Loading…</div>
                  ) : (
                    <select
                      id="location"
                      className="w-full px-3 py-2.5 text-sm border border-border rounded-lg bg-input focus:outline-none focus:ring-2 focus:ring-ring"
                      {...register('location')}
                    >
                      {rooms.map((r) => (
                        <option key={r.id} value={r.name}>{r.name}</option>
                      ))}
                    </select>
                  )}
                </div>
                <div className="space-y-1">
                  <label htmlFor="addedBy" className="block text-sm font-medium text-foreground">
                    Added By
                  </label>
                  {loadingConfig ? (
                    <div className="w-full px-3 py-2.5 text-sm border border-border rounded-lg bg-input text-muted-foreground">Loading…</div>
                  ) : (
                    <select
                      id="addedBy"
                      className="w-full px-3 py-2.5 text-sm border border-border rounded-lg bg-input focus:outline-none focus:ring-2 focus:ring-ring"
                      {...register('addedBy')}
                    >
                      {members.map((m) => (
                        <option key={m.id} value={m.name}>{m.name}</option>
                      ))}
                    </select>
                  )}
                </div>
              </div>
            </div>

            {/* Pricing card */}
            <div className="bg-card border border-border rounded-xl p-5 space-y-4">
              <h2 className="text-sm font-semibold text-foreground pb-2 border-b border-border">Pricing</h2>
              <div className="space-y-1">
                <label htmlFor="unitPrice" className="block text-sm font-medium text-foreground">
                  Unit Price (Replacement Cost)
                </label>
                <p className="text-xs text-muted-foreground">Enter manually or use AI research on the right</p>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                  <input
                    id="unitPrice"
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    className="w-full pl-7 pr-3 py-2.5 text-sm border border-border rounded-lg bg-input focus:outline-none focus:ring-2 focus:ring-ring font-mono placeholder:text-muted-foreground"
                    {...register('unitPrice')}
                  />
                </div>
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex flex-wrap gap-3 pt-2">
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex items-center gap-2 px-6 py-2.5 bg-primary text-primary-foreground rounded-lg text-sm font-semibold hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-150 active:scale-95"
              >
                {isSubmitting ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
                Save &amp; Go to Inventory
              </button>
              <button
                type="button"
                onClick={handleSaveAndAddAnother}
                disabled={isSubmitting}
                className="flex items-center gap-2 px-5 py-2.5 border border-border rounded-lg text-sm font-medium text-foreground hover:bg-secondary disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-150 active:scale-95"
              >
                {isSubmitting ? <Loader2 size={15} className="animate-spin" /> : null}
                Save &amp; Add Another
              </button>
            </div>
          </div>

          {/* Right: Price research panel */}
          <div className="xl:col-span-1">
            <PriceResearchPanel
              itemName={watchedName}
              research={priceResearch}
              isLoading={isResearching}
              onResearch={handleResearchPrice}
            />
          </div>
        </div>
      </form>

      {showImportModal && (
        <ImportItemsModal
          rooms={rooms}
          members={members}
          claimId={activeClaim?.id}
          onClose={() => setShowImportModal(false)}
          onImportComplete={handleImportComplete}
        />
      )}
    </div>
  );
}